import { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuthStore } from '../stores/auth';
import { api } from '../api/client';
import type { User } from '../api/types';

interface Props {
  /**
   * Optional right key the user must hold to access the route. If omitted, the
   * route is open to any authenticated user. Rights-based gating mirrors the
   * server's policy framework (.RequireRight on the matching endpoint).
   */
  requireRight?: string;
}

/**
 * Auth gate for protected routes. Three layers:
 *
 *  1. <b>No token</b> → bounce to `/` (the public holding page), remembering
 *     the intended destination in `location.state.from` so the login flow can
 *     return the user after they sign in.
 *  2. <b>Token but server says it's stale</b> → kicked out by the axios 401
 *     interceptor (logout + replace to /login). While this guard is checking
 *     <c>/api/auth/validate</c> we render a spinner instead of the outlet so
 *     no protected content flashes for an unauthenticated session — the
 *     symptom that previously let "Welcome, user" render on a dead token
 *     until the user clicked something that hit the API.
 *  3. <b>Authenticated but missing <c>requireRight</c></b> → bounce to
 *     `/home`. The user *is* signed in, just lacks the right to view this
 *     specific page.
 *
 * The validation call also overwrites the cached user with the server's
 * current view, so role / right changes the SP made since last login are
 * picked up automatically — no logout/login dance.
 *
 * Only the <b>outer</b> guard (no <c>requireRight</c>) validates with the
 * server; per-page inner guards trust the just-refreshed token and only
 * check the right-key. Navigating between pages doesn't re-hit /validate.
 */
export function ProtectedRoute({ requireRight }: Props) {
  const token = useAuthStore((s) => s.token);
  const setSession = useAuthStore((s) => s.setSession);
  const hasRight = useAuthStore((s) => s.hasRight);
  const location = useLocation();

  // Inner per-page guards skip the network round-trip — the outer guard
  // already verified the token by the time we mount.
  const needsValidation = !requireRight && !!token;
  const [validating, setValidating] = useState(needsValidation);

  useEffect(() => {
    if (!needsValidation) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get<{ valid: boolean; user: User }>('/api/auth/validate');
        if (cancelled) return;
        if (data.valid && data.user) {
          // Refresh the cached session with the latest server view — picks
          // up role + right grants the SP made since last login.
          setSession(token!, data.user);
        }
      } catch {
        // 401 already handled by the axios interceptor (logout + redirect
        // to /login). For any non-401 (network blip etc.), fail open: the
        // next real API call will surface a clearer error.
      } finally {
        if (!cancelled) setValidating(false);
      }
    })();
    return () => { cancelled = true; };
  // Re-run only when the token actually changes (login / logout / impersonate).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (!token) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }
  if (validating) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '60vh',
      }}>
        <CircularProgress />
      </Box>
    );
  }
  if (requireRight && !hasRight(requireRight)) {
    return <Navigate to="/home" replace />;
  }
  return <Outlet />;
}
